import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { motion } from "framer-motion";
import PageHeader from "./PageHeader";

const FoldersPage = () => {
  useEffect(() => {
    // Update document title and meta description for SEO
    document.title = "ChatGPT Folders Extension | Organize Your Chats";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Easily organize your ChatGPT chats into folders with Power-Up. Create, rename, and move chats fast – all in the sidebar.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            ChatGPT Folders – Organize Your Conversations
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Finally, a way to organize your ChatGPT conversations! Create folders, move chats, and keep your work organized – all within ChatGPT's native interface.
          </p>
        </motion.div>

        {/* Problem Statement */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">The Problem: ChatGPT Has No Folder System</h2>
          <p className="text-gray-700 mb-6">
            If you're a heavy ChatGPT user, you know the frustration of having dozens of conversations scattered across your chat history. Finding that important conversation from last week becomes a nightmare of endless scrolling and searching.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-red-50 p-6 rounded-lg">
              <h3 className="font-semibold text-red-800 mb-3">❌ Default ChatGPT Experience</h3>
              <ul className="text-red-700 space-y-2">
                <li>• All chats in one long list</li>
                <li>• No way to group conversations</li>
                <li>• Hard to find specific chats</li>
                <li>• No organization by project or topic</li>
              </ul>
            </div>
            <div className="bg-green-50 p-6 rounded-lg">
              <h3 className="font-semibold text-green-800 mb-3">✅ With ChatGPT Power-Up Folders</h3>
              <ul className="text-green-700 space-y-2">
                <li>• Create unlimited folders</li>
                <li>• Drag & drop chats into folders</li>
                <li>• Organize by project, client, or topic</li>
                <li>• Quick access to related conversations</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Feature Screenshot */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">See ChatGPT Folders in Action</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* BEFORE */}
            <div className="text-center">
              <div className="relative rounded-xl overflow-hidden shadow-lg max-w-sm mx-auto">
                <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
                  BEFORE
                </div>
                <img 
                  src="/folders-before.png" 
                  alt="Default ChatGPT interface without folders - showing unorganized chat list" 
                  className="w-full h-auto"
                />
              </div>
              <p className="text-sm text-gray-600 mt-3">Default ChatGPT - No organization</p>
            </div>
            
            {/* AFTER */}
            <div className="text-center">
              <div className="relative rounded-xl overflow-hidden shadow-lg max-w-sm mx-auto">
                <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
                  AFTER
                </div>
                <img 
                  src="/folders.png" 
                  alt="ChatGPT Power-Up showing organized conversation folders in the sidebar" 
                  className="w-full h-auto object-cover"
                  style={{ maxHeight: '495px', objectPosition: 'center 50%' }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-3">With ChatGPT Power-Up - Organized folders</p>
            </div>
          </div>
        </motion.div>

        {/* How It Works */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">How ChatGPT Folders Work</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Create Folders</h3>
              <p className="text-gray-600">Click the "+" button in the sidebar to create new folders. Name them whatever makes sense for your workflow.</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Move Chats</h3>
              <p className="text-gray-600">Drag any conversation into a folder, or use the context menu to organize chats quickly and easily.</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Stay Organized</h3>
              <p className="text-gray-600">Access your organized conversations instantly. No more scrolling through hundreds of chats to find what you need.</p>
            </div>
          </div>
        </motion.div>

        {/* Use Cases */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Organize Chats by Projects, Clients, or Topics</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3">💼 Project Management</h3>
              <p className="text-gray-600 text-sm">Create folders for each project. Keep all related conversations, brainstorming sessions, and planning chats together.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3">👥 Client Work</h3>
              <p className="text-gray-600 text-sm">Organize conversations by client. Easy access to all client-related discussions and deliverables.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3">📚 Study & Learning</h3>
              <p className="text-gray-600 text-sm">Group study sessions, research questions, and learning materials by subject or topic.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3">🎯 Marketing Campaigns</h3>
              <p className="text-gray-600 text-sm">Keep campaign brainstorming, content creation, and strategy discussions organized by campaign.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3">💻 Development</h3>
              <p className="text-gray-600 text-sm">Organize coding help, debugging sessions, and technical discussions by project or technology.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3">📝 Content Creation</h3>
              <p className="text-gray-600 text-sm">Group blog posts, social media content, and creative writing sessions by theme or platform.</p>
            </div>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Are there folders in ChatGPT?</h3>
              <p className="text-gray-600">No, ChatGPT doesn't have a built-in folder system. That's exactly why we created ChatGPT Power-Up – to add this essential organization feature that power users need.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">How do ChatGPT folders improve productivity?</h3>
              <p className="text-gray-600">By organizing your conversations into logical groups, you can find information faster, maintain context across related discussions, and avoid the frustration of scrolling through hundreds of chats.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Can I move chats between folders?</h3>
              <p className="text-gray-600">Yes! You can drag and drop chats between folders, or use the context menu to move conversations. Your organization can evolve as your projects and needs change.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Do I need to create an account?</h3>
              <p className="text-gray-600">No account required! ChatGPT Power-Up works instantly after installation. All your folders and organization are stored locally in your browser.</p>
            </div>
          </div>
        </motion.div>

        {/* Benefits */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-8 mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Why Power Users Love ChatGPT Folders</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-green-500 mr-3 mt-1">✓</span>
                <span className="text-gray-700">Create unlimited folders for any organization system</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-3 mt-1">✓</span>
                <span className="text-gray-700">Drag & drop interface for intuitive organization</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-3 mt-1">✓</span>
                <span className="text-gray-700">Rename folders anytime to match your workflow</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-3 mt-1">✓</span>
                <span className="text-gray-700">Works seamlessly within ChatGPT's native UI</span>
              </li>
            </ul>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-green-500 mr-3 mt-1">✓</span>
                <span className="text-gray-700">No learning curve - feels like native ChatGPT</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-3 mt-1">✓</span>
                <span className="text-gray-700">Keyboard shortcuts for power users</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-3 mt-1">✓</span>
                <span className="text-gray-700">Export your folder structure for backup</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-3 mt-1">✓</span>
                <span className="text-gray-700">Free Chrome extension - no subscription needed</span>
              </li>
            </ul>
          </div>
        </motion.div>

        {/* Related Content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.4 }}
          className="bg-white rounded-xl p-8 mb-12 shadow-lg"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Explore More ChatGPT Power-Up Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/chatgpt-prompt-generator" className="block">
              <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition-all">
                <h3 className="font-semibold text-gray-900 mb-2">ChatGPT Prompt Generator</h3>
                <p className="text-gray-600 text-sm">Save and reuse your best prompts with our powerful prompt generator.</p>
              </div>
            </Link>
            <Link to="/chatgpt-prompt-templates" className="block">
              <div className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition-all">
                <h3 className="font-semibold text-gray-900 mb-2">ChatGPT Prompt Templates</h3>
                <p className="text-gray-600 text-sm">Access pre-built templates and create your own for common tasks.</p>
              </div>
            </Link>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.6 }}
          className="text-center bg-gradient-to-r from-green-600 to-blue-600 rounded-xl p-8 text-white"
        >
          <h2 className="text-3xl font-bold mb-4">Ready to Organize Your ChatGPT Conversations?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of users who've transformed their ChatGPT workflow with folders
          </p>
          <a
            href="https://chromewebstore.google.com/detail/chatgpt-power-up/ooleaojggfoigcdkodigbcjnabidihgi"
            className="inline-block"
          >
            <Button className="bg-white text-green-600 hover:bg-gray-100 text-lg px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
              ➕ Add to Chrome – It's Free
            </Button>
          </a>
          <p className="text-sm opacity-75 mt-4">Free • No account required • Works instantly</p>
        </motion.div>
      </div>
    </div>
  );
};

export default FoldersPage; 